import { Component, createRef } from "react";
import { connect } from "react-redux";
import { type Field, Form, type FormErrors, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";

import {
  getFormError,
  getFormWarning,
  getSelectedOption,
  handleTabInForm,
  simpleDropdownFieldValidation,
} from "../../../app-utils/form.js";
import { type Playbook, type SampleStatus } from "../../../graphql.js";
import {
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { PLAYBOOK_TYPE, TRACKER_STATUS_CLASS_ID } from "../../../settings/settings.js";
import TabFence from "../../core/forms/TabFence.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type IPlaybookConfigSampleStatusScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigSampleStatusScreenAppStateProps {
  globalFormError: string;
  globalFormWarning: string;
  initialValues: Partial<FormData>;
  sampleStatuses: SampleStatus[];
  selectedPlaybookType: PLAYBOOK_TYPE;
}

interface IPlaybookConfigSampleStatusScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
  savePlaybook: () => Promise<Playbook>;
}

export interface IPlaybookConfigSampleStatusScreenProps
  extends IPlaybookConfigSampleStatusScreenAppStateProps,
    IPlaybookConfigSampleStatusScreenDispatchProps,
    IPlaybookConfigSampleStatusScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigSampleStatusScreenOwnProps,
): IPlaybookConfigSampleStatusScreenAppStateProps {
  const sampleStatuses: SampleStatus[] = state.sampleAdmin.sampleType.sampleStatuses;
  const playbookFormState: Partial<IPlaybookConfigState> = state.playbookAdminState.playbookConfigState;

  return {
    globalFormError: getFormError(PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    globalFormWarning: getFormWarning(PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    initialValues: {
      [PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE]: getSelectedOption<SampleStatus>(
        sampleStatuses,
        playbookFormState.statusAfterCompletion,
        (status) => status.id,
      ),
      [PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION]: getSelectedOption<SampleStatus>(
        sampleStatuses,
        playbookFormState.statusAfterSubmission,
        (status) => status.id,
      ),
      [PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW]: getSelectedOption<SampleStatus>(
        sampleStatuses,
        playbookFormState.statusWhileRunning,
        (status) => status.id,
      ),
      [PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_ERROR]: getSelectedOption<SampleStatus>(
        sampleStatuses,
        playbookFormState.statusAfterError,
        (status) => status.id,
      ),
    },
    sampleStatuses: sampleStatuses,
    selectedPlaybookType: state.playbookAdminState.playbookConfigState.playbookType,
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigSampleStatusScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
    savePlaybook: () => dispatch(savePlaybook()),
  };
}

class PlaybookConfigSampleStatusScreenImpl extends Component<IPlaybookConfigSampleStatusScreenProps> {
  public static PLAYBOOK_CREATION_FORM_NAME = "PLAYBOOK_CREATION_SAMPLE_STATUS";
  // field identifiers
  public static PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION = "PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION";
  public static PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW = "PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW";
  public static PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE = "PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE";
  public static PLAYBOOK_CREATION_FIELD_ERROR = "PLAYBOOK_CREATION_FIELD_ERROR";

  private lastTabRef = createRef<Field>();
  private firstTabRef = createRef<Field>();

  constructor(props) {
    super(props);
  }

  handleSubmitData = (values) => {
    // at this stage the validation has succeeded

    const playbookCreationState: Partial<IPlaybookConfigState> = {
      statusAfterSubmission: values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION].id,
      statusWhileRunning: values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW]?.id,
      statusAfterCompletion: values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE]?.id,
      statusAfterError: values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_ERROR]?.id,
    };

    this.props.updatePlaybookCreationState(playbookCreationState);

    return this.props
      .savePlaybook()
      .then(() => this.props.proceedWithNextStep())
      .catch((error: Error) => {
        throw new SubmissionError({ _error: error.message });
      });
  };

  getStatusLabel = (option: SampleStatus) => {
    return `${option.name} ${option.statusClass ? `("${option.statusClass.name}" class)` : ``}`;
  };

  render() {
    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Save Playbook",
            isLoading: !!this.props.asyncValidating || this.props.submitting,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form className="form-horizontal" onSubmit={this.props.handleSubmit(this.handleSubmitData)}>
          <div onKeyDownCapture={handleTabInForm}>
            <TabFence targetRef={this.lastTabRef} />

            <ReactSelectReduxFormField
              key={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION}
              name={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION}
              getOptionValue={(option: SampleStatus) => option.id}
              getOptionLabel={this.getStatusLabel}
              horizontalLayout={true}
              isClearable={false}
              isSearchable={false}
              label="After the Playbook is submitted"
              component={ReactSelectField}
              isLoading={false}
              options={this.props.sampleStatuses.filter((s) => s.statusClass.id === TRACKER_STATUS_CLASS_ID.READY)}
              isDisabled={false}
              validate={simpleDropdownFieldValidation}
            />

            {this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES && (
              <>
                <ReactSelectReduxFormField
                  key={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW}
                  name={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW}
                  getOptionValue={(option: SampleStatus) => option.id}
                  getOptionLabel={this.getStatusLabel}
                  horizontalLayout={true}
                  isClearable={false}
                  isSearchable={false}
                  label="While the NGS workflow is running"
                  component={ReactSelectField}
                  isLoading={false}
                  options={this.props.sampleStatuses}
                  isDisabled={false}
                  validate={simpleDropdownFieldValidation}
                />
                <ReactSelectReduxFormField
                  key={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE}
                  name={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE}
                  getOptionValue={(option: SampleStatus) => option.id}
                  getOptionLabel={this.getStatusLabel}
                  horizontalLayout={true}
                  isClearable={false}
                  isSearchable={false}
                  label="After NGS data processing is complete"
                  component={ReactSelectField}
                  isLoading={false}
                  options={this.props.sampleStatuses}
                  isDisabled={false}
                  validate={simpleDropdownFieldValidation}
                />
                <ReactSelectReduxFormField
                  key={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_ERROR}
                  name={PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_ERROR}
                  getOptionValue={(option: SampleStatus) => option.id}
                  getOptionLabel={this.getStatusLabel}
                  horizontalLayout={true}
                  isClearable={false}
                  isSearchable={false}
                  label="In case of an error during NGS data processing"
                  component={ReactSelectField}
                  isLoading={false}
                  options={this.props.sampleStatuses}
                  isDisabled={false}
                  validate={simpleDropdownFieldValidation}
                />
              </>
            )}
            <TabFence targetRef={this.firstTabRef} />
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }

  static validate = (values: FormData, _props: IPlaybookConfigSampleStatusScreenProps): FormErrors<FormData, any> => {
    const errors = {};

    const statusAfterSubmission: SampleStatus =
      values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION];
    const statusWhileRunning: SampleStatus =
      values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW];
    const statusAfterCompletion: SampleStatus =
      values[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_COMPLETE];

    if (
      statusAfterSubmission &&
      statusWhileRunning &&
      statusAfterCompletion &&
      statusAfterSubmission.id === statusWhileRunning.id
    ) {
      errors[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_AFTER_SUBMISSION] =
        "A status can only be selected once.";
      errors[PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FIELD_RUNNING_WORKFLOW] =
        "A status can only be selected once.";
    }
    // ...etc...

    return errors;
  };
}

const PlaybookConfigSampleStatusScreen = connect<
  IPlaybookConfigSampleStatusScreenAppStateProps,
  IPlaybookConfigSampleStatusScreenDispatchProps,
  IPlaybookConfigSampleStatusScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: PlaybookConfigSampleStatusScreenImpl.PLAYBOOK_CREATION_FORM_NAME,
    // validate: PlaybookConfigSampleStatusScreenImpl.validate, // for global validation
    destroyOnUnmount: false,
  })(PlaybookConfigSampleStatusScreenImpl) as any,
);

export { PlaybookConfigSampleStatusScreen };
